import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  AlertTriangle, XCircle, CheckCircle2, Clock, FileText,
  Plus, Send, Gavel, TrendingUp, AlertCircle, RefreshCw
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const statusConfig = {
  draft: { label: "Draft", color: "bg-slate-500/20 text-slate-400 border-slate-500/30", icon: FileText },
  submitted: { label: "Submitted", color: "bg-blue-500/20 text-blue-400 border-blue-500/30", icon: Send },
  under_review: { label: "Under Review", color: "bg-amber-500/20 text-amber-400 border-amber-500/30", icon: Clock },
  approved: { label: "Approved", color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30", icon: CheckCircle2 },
  denied: { label: "Denied", color: "bg-red-500/20 text-red-400 border-red-500/30", icon: XCircle },
  withdrawn: { label: "Withdrawn", color: "bg-slate-500/20 text-slate-400 border-slate-500/30", icon: RefreshCw }
};

const appealLevels = {
  first_level: "First Level Appeal",
  second_level: "Second Level Appeal",
  external_review: "External Review",
  expedited: "Expedited Appeal"
};

export default function Appeals() {
  const { user, organization, isAdmin } = useAuth();
  const [denials, setDenials] = useState([]);
  const [appeals, setAppeals] = useState([]);
  const [stats, setStats] = useState({});
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("denials");
  
  // Create appeal dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedDenial, setSelectedDenial] = useState(null);
  const [appealReason, setAppealReason] = useState("");
  const [appealLevel, setAppealLevel] = useState("first_level");
  const [additionalInfo, setAdditionalInfo] = useState("");
  const [peerToPeer, setPeerToPeer] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [denialsRes, appealsRes, statsRes, catsRes] = await Promise.all([
        axios.get(`${API}/denials`, { withCredentials: true }),
        axios.get(`${API}/appeals`, { withCredentials: true }),
        axios.get(`${API}/denials/stats`, { withCredentials: true }),
        axios.get(`${API}/denials/categories`, { withCredentials: true })
      ]);
      
      setDenials(denialsRes.data);
      setAppeals(appealsRes.data);
      setStats(statsRes.data);
      setCategories(catsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      // Don't show error if no denials exist yet
      if (error.response?.status !== 404) {
        toast.error("Failed to load appeals data");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateAppeal = (denial) => {
    setSelectedDenial(denial);
    setAppealReason("");
    setAppealLevel("first_level");
    setAdditionalInfo("");
    setPeerToPeer(false);
    setCreateOpen(true);
  };

  const handleCreateAppeal = async () => {
    if (!appealReason.trim()) {
      toast.error("Please provide an appeal reason");
      return;
    }

    setCreating(true);
    try {
      await axios.post(
        `${API}/appeals`,
        {
          denial_id: selectedDenial.denial_id,
          pa_id: selectedDenial.pa_id,
          appeal_reason: appealReason,
          appeal_level: appealLevel,
          additional_clinical_info: additionalInfo,
          peer_to_peer_requested: peerToPeer
        },
        { withCredentials: true }
      );
      
      toast.success("Appeal created successfully");
      setCreateOpen(false);
      fetchData();
    } catch (error) {
      console.error("Create appeal error:", error);
      toast.error(error.response?.data?.detail || "Failed to create appeal");
    } finally {
      setCreating(false);
    }
  };

  const handleSubmitAppeal = async (appealId) => {
    try {
      await axios.post(`${API}/appeals/${appealId}/submit`, {}, { withCredentials: true });
      toast.success("Appeal submitted for review");
      fetchData();
    } catch (error) {
      console.error("Submit appeal error:", error);
      toast.error(error.response?.data?.detail || "Failed to submit appeal");
    }
  };

  const getCategoryLabel = (category) => {
    return categories[category]?.label || category;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-white" data-testid="appeals-title">
            Denial & Appeals Management
          </h1>
          <p className="text-slate-400 mt-1">
            Track denials, file appeals, and manage the appeals process
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-500/20 rounded-lg">
                  <XCircle className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.total_denials || 0}</p>
                  <p className="text-sm text-slate-400">Total Denials</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-500/20 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.appealable || 0}</p>
                  <p className="text-sm text-slate-400">Appealable</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <Clock className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.active_appeals || 0}</p>
                  <p className="text-sm text-slate-400">Active Appeals</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-500/20 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.successful_appeals || 0}</p>
                  <p className="text-sm text-slate-400">Successful</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.appeal_success_rate || 0}%</p>
                  <p className="text-sm text-slate-400">Success Rate</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-800 border border-slate-700">
            <TabsTrigger 
              value="denials" 
              className="data-[state=active]:bg-emerald-600"
              data-testid="denials-tab"
            >
              <XCircle className="w-4 h-4 mr-2" />
              Denials ({denials.length})
            </TabsTrigger>
            <TabsTrigger 
              value="appeals" 
              className="data-[state=active]:bg-emerald-600"
              data-testid="appeals-tab"
            >
              <Gavel className="w-4 h-4 mr-2" />
              Appeals ({appeals.length})
            </TabsTrigger>
          </TabsList>

          {/* Denials Tab */}
          <TabsContent value="denials">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Denied PA Requests</CardTitle>
                <CardDescription className="text-slate-400">
                  Review denied requests and file appeals
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8">
                    <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                    <p className="text-slate-400">Loading...</p>
                  </div>
                ) : denials.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle2 className="w-12 h-12 text-emerald-500/50 mx-auto mb-3" />
                    <p className="text-slate-400 text-lg">No denials found</p>
                    <p className="text-slate-500 text-sm mt-1">
                      All your PA requests have been approved
                    </p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700">
                        <TableHead className="text-slate-400">PA ID</TableHead>
                        <TableHead className="text-slate-400">Patient</TableHead>
                        <TableHead className="text-slate-400">Service</TableHead>
                        <TableHead className="text-slate-400">Category</TableHead>
                        <TableHead className="text-slate-400">Reason</TableHead>
                        <TableHead className="text-slate-400">Date</TableHead>
                        <TableHead className="text-slate-400">Status</TableHead>
                        <TableHead className="text-slate-400 text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {denials.map((denial) => (
                        <TableRow 
                          key={denial.denial_id} 
                          className="border-slate-700"
                          data-testid={`denial-row-${denial.denial_id}`}
                        >
                          <TableCell className="font-mono text-sm text-slate-300">
                            {denial.pa_id}
                          </TableCell>
                          <TableCell className="text-white">
                            {denial.patient_name || "Unknown"}
                          </TableCell>
                          <TableCell className="text-slate-300">
                            {denial.service_type || "-"}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="border-red-500/30 text-red-400">
                              {getCategoryLabel(denial.denial_category)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-slate-400 max-w-[200px] truncate">
                            {denial.denial_reason}
                          </TableCell>
                          <TableCell className="text-slate-400">
                            {denial.denial_date 
                              ? new Date(denial.denial_date).toLocaleDateString() 
                              : "-"}
                          </TableCell>
                          <TableCell>
                            {denial.has_active_appeal ? (
                              <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 border">
                                <Clock className="w-3 h-3 mr-1" />
                                Appeal Pending
                              </Badge>
                            ) : denial.is_appealable ? (
                              <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 border">
                                Appealable
                              </Badge>
                            ) : (
                              <Badge className="bg-slate-500/20 text-slate-400 border-slate-500/30 border">
                                Not Appealable
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {denial.is_appealable && !denial.has_active_appeal && (
                              <Button
                                size="sm"
                                onClick={() => handleOpenCreateAppeal(denial)}
                                className="bg-emerald-600 hover:bg-emerald-700"
                                data-testid={`appeal-btn-${denial.denial_id}`}
                              >
                                <Plus className="w-4 h-4 mr-1" />
                                Appeal
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Appeals Tab */}
          <TabsContent value="appeals">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Appeal Requests</CardTitle>
                <CardDescription className="text-slate-400">
                  Track the status of your appeal requests
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8">
                    <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                    <p className="text-slate-400">Loading...</p>
                  </div>
                ) : appeals.length === 0 ? (
                  <div className="text-center py-12">
                    <Gavel className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                    <p className="text-slate-400 text-lg">No appeals filed</p>
                    <p className="text-slate-500 text-sm mt-1">
                      File an appeal from the Denials tab
                    </p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700">
                        <TableHead className="text-slate-400">Appeal ID</TableHead>
                        <TableHead className="text-slate-400">Patient</TableHead>
                        <TableHead className="text-slate-400">Level</TableHead>
                        <TableHead className="text-slate-400">Status</TableHead>
                        <TableHead className="text-slate-400">Submitted</TableHead>
                        <TableHead className="text-slate-400">Outcome</TableHead>
                        <TableHead className="text-slate-400 text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {appeals.map((appeal) => {
                        const statusInfo = statusConfig[appeal.status] || statusConfig.draft;
                        const StatusIcon = statusInfo.icon;
                        
                        return (
                          <TableRow 
                            key={appeal.appeal_id} 
                            className="border-slate-700"
                            data-testid={`appeal-row-${appeal.appeal_id}`}
                          >
                            <TableCell className="font-mono text-sm text-slate-300">
                              {appeal.appeal_id}
                            </TableCell>
                            <TableCell className="text-white">
                              {appeal.patient_name || "Unknown"}
                            </TableCell>
                            <TableCell className="text-slate-300">
                              {appealLevels[appeal.appeal_level] || appeal.appeal_level}
                            </TableCell>
                            <TableCell>
                              <Badge className={`${statusInfo.color} border`}>
                                <StatusIcon className="w-3 h-3 mr-1" />
                                {statusInfo.label}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-slate-400">
                              {appeal.submitted_date 
                                ? new Date(appeal.submitted_date).toLocaleDateString() 
                                : "Not submitted"}
                            </TableCell>
                            <TableCell>
                              {appeal.outcome ? (
                                <Badge className={
                                  appeal.outcome === "approved" 
                                    ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30 border"
                                    : "bg-red-500/20 text-red-400 border-red-500/30 border"
                                }>
                                  {appeal.outcome === "approved" ? "Approved" : "Denied"}
                                </Badge>
                              ) : (
                                <span className="text-slate-500">Pending</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              {appeal.status === "draft" && (
                                <Button
                                  size="sm"
                                  onClick={() => handleSubmitAppeal(appeal.appeal_id)}
                                  className="bg-blue-600 hover:bg-blue-700"
                                >
                                  <Send className="w-4 h-4 mr-1" />
                                  Submit
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Create Appeal Dialog */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="bg-slate-800 border-slate-700 max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-white">File an Appeal</DialogTitle>
              <DialogDescription className="text-slate-400">
                Appeal the denial for PA {selectedDenial?.pa_id}
              </DialogDescription>
            </DialogHeader>
            
            {selectedDenial && (
              <div className="space-y-4">
                {/* Denial Summary */}
                <Card className="bg-slate-900/50 border-slate-700">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-red-500/20 rounded-lg">
                        <XCircle className="w-5 h-5 text-red-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium">Denial Reason</p>
                        <p className="text-slate-400 text-sm mt-1">
                          {selectedDenial.denial_reason}
                        </p>
                        <Badge 
                          variant="outline" 
                          className="mt-2 border-red-500/30 text-red-400"
                        >
                          {getCategoryLabel(selectedDenial.denial_category)}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Appeal Level */}
                <div className="space-y-2">
                  <Label className="text-slate-300">Appeal Level</Label>
                  <Select value={appealLevel} onValueChange={setAppealLevel}>
                    <SelectTrigger className="bg-slate-900/50 border-slate-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {Object.entries(appealLevels).map(([key, label]) => (
                        <SelectItem key={key} value={key} className="text-slate-300">
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Appeal Reason */}
                <div className="space-y-2">
                  <Label className="text-slate-300">Appeal Reason *</Label>
                  <Textarea
                    value={appealReason}
                    onChange={(e) => setAppealReason(e.target.value)}
                    placeholder="Explain why you believe this denial should be overturned..."
                    className="bg-slate-900/50 border-slate-600 text-white min-h-[100px]"
                    required
                  />
                </div>

                {/* Additional Clinical Info */}
                <div className="space-y-2">
                  <Label className="text-slate-300">Additional Clinical Information</Label>
                  <Textarea
                    value={additionalInfo}
                    onChange={(e) => setAdditionalInfo(e.target.value)}
                    placeholder="Provide any additional clinical documentation or rationale..."
                    className="bg-slate-900/50 border-slate-600 text-white min-h-[80px]"
                  />
                </div>

                {/* Peer-to-Peer Request */}
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="peerToPeer"
                    checked={peerToPeer}
                    onChange={(e) => setPeerToPeer(e.target.checked)}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-900 text-emerald-500"
                  />
                  <Label htmlFor="peerToPeer" className="text-slate-300 cursor-pointer">
                    Request peer-to-peer review with Medical Director
                  </Label>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setCreateOpen(false)}
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleCreateAppeal}
                disabled={creating || !appealReason.trim()}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {creating ? "Creating..." : "Create Appeal"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
